import { Injectable } from '@angular/core';
import { ORDER_BOOK_SEARCH_URL_PARAM } from '@epicuro-next/constants/features/order-book';
import {
  FiltersService,
  OrderBookConfigurationKeys,
  OrderBookConfigurations,
  ORDER_BOOK_NAMESPACE,
} from '@epicuro-next/data/order-book';
import { FiltersFieldIds } from '@epicuro-next/models/order-book';
import { selectConfigurationValueFrom } from '@epicuro-next/platform/configuration';
import { selectActiveAccount } from '@epicuro-next/state/accounts-state';
import {
  selectQueryParams,
  updateQueryParameters,
} from '@epicuro-next/state/router-store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import {
  FilterQuery,
  FilterValue,
  OrderFilter,
} from 'graphql-api/baskets-api-interfaces';
import { combineLatest, forkJoin, Observable, of } from 'rxjs';
import {
  catchError,
  map,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs/operators';

import {
  selectActiveOrderBookFilters,
  selectAvailableYearsFilter,
  selectIsFilterYearEnabled,
  selectIsMultipleFilter,
  selectOrderBookFilters,
  selectOrderBookFiltersLoaded,
  selectOrderFiltersQuery,
  selectQueryParamsByActiveFilters,
} from '../selectors/filters.selectors';
import { getYearFilterValue } from '../shared/year-filter/get-year-filter-value';
import { makeYearFilterQueryItem } from '../shared/year-filter/make-year-filter-query-item';

import * as filtersActions from './../actions/filters.actions';
import { getActiveFiltersAfterFilterToggle } from './get-active-filters-after-filter-toggle/get-active-filters-after-filter-toggle';
import { mapUrlParamsToFilters } from './map-url-params-to-filters/map-url-params-to-filters';

@Injectable()
export class FiltersEffects {
  private readonly isAccountFilterEnabled$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        OrderBookConfigurationKeys.isAccountFilterEnabled,
        OrderBookConfigurations
      >(
        ORDER_BOOK_NAMESPACE,
        OrderBookConfigurationKeys.isAccountFilterEnabled,
        false,
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private filters: FiltersService,
    private store: Store,
  ) {}

  public loadFilters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(filtersActions.loadFilters),
      withLatestFrom(this.isAccountFilterEnabled$),
      switchMap(([_, isAccountFilterEnabled]) => {
        return forkJoin([
          this.getAvailableYears().pipe(
            withLatestFrom(
              this.store.pipe(select(selectIsFilterYearEnabled)),
              this.store.pipe(select(selectOrderFiltersQuery)),
              this.store.pipe(select(selectOrderBookFiltersLoaded)),
              this.store.pipe(select(selectOrderBookFilters)),
              this.store.pipe(select(selectActiveAccount)),
            ),
          ),
          isAccountFilterEnabled ? this.getAvailableAccounts() : of([]),
        ]);
      }),
      switchMap(
        ([
          [
            availableYears,
            canFilterByYear,
            query,
            filtersLoaded,
            allFilters,
            activeAccount,
          ],
          accounts,
        ]) => {
          let filtersQuery: FilterQuery;

          if (filtersLoaded) {
            filtersQuery = query;
          } else {
            // load initial filters with delivery date
            const yearFilterQuery = makeYearFilterQueryItem(
              getYearFilterValue(availableYears, [], canFilterByYear),
            );

            filtersQuery = {
              filters: [yearFilterQuery],
            };
          }

          return this.filters.getFilters(filtersQuery).pipe(
            map(({ total, filters }) => {
              const orderedFilters: OrderFilter[] = [...filters];
              if (canFilterByYear) {
                orderedFilters.splice(
                  filters.findIndex(
                    (f) => f.fieldId === FiltersFieldIds.DeliveryMonth,
                  ),
                  0,
                  ...availableYears,
                );
              }

              if (
                allFilters.some(
                  (item) => item.fieldId === FiltersFieldIds.AccountId,
                ) &&
                !!accounts.length
              ) {
                const responseAccountsIndex = orderedFilters.findIndex(
                  (f: OrderFilter) => f.fieldId === FiltersFieldIds.AccountId,
                );

                const mergedValues = this.mergeAccounts([
                  activeAccount
                    ? {
                        displayName: activeAccount.name,
                        value: activeAccount.accountId,
                      }
                    : undefined,
                  ...accounts.flatMap((item) => item.values),
                ] as FilterValue[]);

                orderedFilters.splice(responseAccountsIndex, 1, {
                  ...orderedFilters[responseAccountsIndex],
                  values: mergedValues,
                });
              }

              return filtersActions.loadFiltersSuccess({
                total,
                filters: orderedFilters,
              });
            }),
            catchError((error) =>
              of(filtersActions.loadFiltersFailure({ error: error.message })),
            ),
          );
        },
      ),
    ),
  );

  public reloadFiltersOnFilterChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        filtersActions.setActiveFilters,
        filtersActions.setInitialSelectedFiltersFromUrlSuccess,
        filtersActions.setSearchQuery,
        filtersActions.clearFilterById,
      ),
      map(() => filtersActions.loadFilters()),
    ),
  );

  public setInitialSelectedFiltersFromUrl$ = createEffect(() =>
    this.actions$.pipe(
      ofType(filtersActions.setInitialSelectedFiltersFromUrl),
      concatLatestFrom(() => [
        this.store.pipe(select(selectQueryParams)),
        this.store.pipe(select(selectOrderBookFilters)),
      ]),
      map(([_, params, filters]) => [
        params[ORDER_BOOK_SEARCH_URL_PARAM] ?? '',
        mapUrlParamsToFilters(params, filters),
      ]),
      map(([search, selectedFilters]) =>
        filtersActions.setInitialSelectedFiltersFromUrlSuccess({
          filters: selectedFilters,
          search,
        }),
      ),
    ),
  );

  public setActiveFiltersOnToggle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(filtersActions.toggleFilter),
      concatLatestFrom(({ filter }) => [
        this.store.pipe(select(selectActiveOrderBookFilters)),
        this.store.pipe(select(selectIsMultipleFilter(filter.fieldId))),
      ]),
      map(([{ filter }, activeFilters, isMultiple]) =>
        getActiveFiltersAfterFilterToggle(activeFilters, filter, isMultiple),
      ),
      map((filters) => filtersActions.setActiveFilters({ filters })),
    ),
  );

  public updateQueryParamsOnActiveFiltersChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        filtersActions.setActiveFilters,
        filtersActions.setSearchQuery,
        filtersActions.clearFilterById,
        filtersActions.setInitialSelectedFiltersFromUrlSuccess,
      ),
      concatLatestFrom(() =>
        this.store.pipe(select(selectQueryParamsByActiveFilters)),
      ),
      map(([_, queryParams]) =>
        updateQueryParameters({
          queryParams,
          queryParamsHandling: 'merge',
        }),
      ),
    ),
  );

  private getAvailableYears(): Observable<OrderFilter[]> {
    return combineLatest([
      this.store.pipe(select(selectIsFilterYearEnabled)),
      this.store.pipe(select(selectAvailableYearsFilter)),
    ]).pipe(
      take(1),
      switchMap(([isEnabled, years]) => {
        if (!isEnabled) {
          return of([]);
        }

        if (years) {
          return of([years]);
        }

        return this.filters.getFilterYears().pipe(catchError(() => of([])));
      }),
    );
  }

  private getAvailableAccounts(): Observable<OrderFilter[]> {
    return this.store.pipe(select(selectOrderFiltersQuery)).pipe(
      take(1),
      switchMap((query) => {
        const withoutAccountQuery = {
          ...query,
          filters: query.filters?.filter(
            (item) => item?.fieldId !== FiltersFieldIds.AccountId,
          ),
        };

        return this.filters
          .getFilterAccounts(withoutAccountQuery)
          .pipe(catchError(() => of([])));
      }),
    );
  }

  private mergeAccounts(values: FilterValue[]) {
    const mergedValues = new Map<string, FilterValue>();
    values.filter(Boolean).forEach((item: FilterValue) => {
      if (mergedValues.has(item.value)) {
        return;
      }

      mergedValues.set(item.value, item);
    });

    return [...mergedValues.values()];
  }
}
